// Draws a 360° (equirectangular) video the way Street View draws a
// panorama: drag to look around, scroll to zoom, the video itself is the
// texture. Plain WebGL, no library: three.js would add ~600 KB to every
// page for one viewer. Used by site_walks_controller for walks filmed with
// a 360° camera.
//
//   const pano = new PanoramaVideo(canvas, video, { onError, onFrame, onTap, onHover })
//   pano.start()  ... pano.stop()  ... pano.destroy()
//
// onFrame(view) runs after every frame with { yaw, pitch, fov, width, height,
// dpr } so something can be drawn over the view in step with it (the walk's
// path, lib/walk_path.js); onTap(x, y) is a click that wasn't a drag, and
// onHover(x, y) the pointer moving without one - both in CSS pixels of the
// canvas.
//
// The video must have been loaded with crossOrigin = "anonymous" (S3 sends
// CORS headers for the app's origin); without it, uploading a frame into a
// texture throws and `start()` returns false so the caller can fall back to
// the flat player.
//
// A timelapse walk (pano.setPictures({ frameSeconds, backward })) is a string
// of pictures a couple of metres apart, each held for frameSeconds of the
// copy. Cut from one to the next it looked choppy (Phelipe 2026-09-25: "the
// walk is a little choppy, it's not fluid", #17), so each new picture glides
// in: the last one leans in toward the way they walked while it fades, and
// the new one settles in from just behind - the feel of Street View's step.
// The two move together (K9-2, Larry 2026-10-13: "like it takes a snapshot
// and then pulls you into it"): the last picture used to grow far past the
// new one, so the hand-off looked like stepping back.
// A frame is only sent to the graphics card when the video shows a new one
// (requestVideoFrameCallback), not every screen refresh, which also keeps
// ordinary video light.

const VERTEX = `
attribute vec2 position;
varying vec2 clip;
void main() {
  clip = position;
  gl_Position = vec4(position, 0.0, 1.0);
}`

const FRAGMENT = `
precision mediump float;
uniform sampler2D frame;
uniform sampler2D previous;
uniform float blend;
uniform float leanNow;
uniform float leanBefore;
uniform vec3 travel;
uniform mat3 levelNow;
uniform mat3 levelBefore;
uniform float yaw;
uniform float pitch;
uniform float tanHalfFov;
uniform float aspect;
varying vec2 clip;
const float PI = 3.141592653589793;
// Where a ray lands on the sphere the video wraps around.
vec2 sphere(vec3 d) {
  return vec2(fract(0.5 + atan(d.x, -d.z) / (2.0 * PI)), 0.5 - asin(clamp(d.y, -1.0, 1.0)) / PI);
}
void main() {
  // The ray through this pixel, in camera space (looking down -Z)...
  vec3 dir = normalize(vec3(clip.x * tanHalfFov * aspect, clip.y * tanHalfFov, -1.0));
  // ...tilted up/down, then turned left/right.
  float cp = cos(pitch); float sp = sin(pitch);
  dir = vec3(dir.x, dir.y * cp - dir.z * sp, dir.y * sp + dir.z * cp);
  float cy = cos(yaw); float sy = sin(yaw);
  dir = vec3(dir.x * cy + dir.z * sy, dir.y, -dir.x * sy + dir.z * cy);
  // Leaning the ray toward the way they walked magnifies what's ahead: a step
  // forward. Away from it (a negative lean): a step back.
  // Each picture turned back level (#6): the ray of a level camera, found in
  // the tilted picture.
  vec4 now = texture2D(frame, sphere(normalize(mix(levelNow * dir, travel, leanNow))));
  if (blend >= 1.0) {
    gl_FragColor = now;
  } else {
    vec4 before = texture2D(previous, sphere(normalize(mix(levelBefore * dir, travel, leanBefore))));
    gl_FragColor = mix(before, now, blend);
  }
}`

const IDENTITY = new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1])

// The glide between two pictures of a timelapse (#17).
const GLIDE_MS = 300 // a little faster (Phelipe 2026-09-25, was 450)
const MIN_GLIDE_MS = 120
const CALM_MS = 180 // reduced motion: a quick plain fade, nothing moves
const JUMP_PICTURES = 2 // a longer jump (the slider, the map) just fades
// How much bigger what's ahead looks one picture on (they're a couple of
// metres apart). A lean `l` toward the way they walked magnifies what's
// ahead by 1 / (1 - l), so a picture shown at size m leans 1 - 1/m.
const STEP_SCALE = 1.2

// Where a glide is `now` (ms): how much of the new picture shows (blend), and
// how far the new and the last picture lean toward the way they walked.
// null when it's over. `fade`: { start, duration, direction (+1 forward, -1
// back), jump (no lean, just the fade) }.
//
// A step forward (K9-2): the last picture grows toward the way they walked
// (1 -> STEP_SCALE) while the new one grows into place from just behind
// (1/STEP_SCALE -> 1) - both by the same amount at every moment, so they stay
// lined up and nothing ever shrinks. A step back runs it the other way. The
// move starts at once and settles (ease-out), and the new picture takes over
// early, so stepping fast stays snappy.
export function glideAt(fade, now) {
  if (!fade) return null
  const t = Math.min(1, Math.max(0, (now - fade.start) / fade.duration))
  if (t >= 1) return null
  const blend = t * (2 - t)
  if (fade.jump) return { blend, leanNow: 0, leanBefore: 0 }
  const moved = 1 - (1 - t) ** 3
  const ahead = fade.direction < 0 ? -1 : 1
  return {
    blend,
    leanBefore: 1 - STEP_SCALE ** (-ahead * moved),
    leanNow: 1 - STEP_SCALE ** (ahead * (1 - moved))
  }
}

// How long a glide runs (ms): a step on a paused walk takes the whole glide;
// playing, it fits inside the time each picture shows. Reduced motion
// (`calm`): a quick plain fade.
export function glideDuration({ paused, frameSeconds, playbackRate = 1, calm = false }) {
  const perPicture = (frameSeconds * 1000) / Math.max(0.1, playbackRate || 1)
  const duration = paused ? GLIDE_MS : Math.max(MIN_GLIDE_MS, Math.min(GLIDE_MS, perPicture * 0.8))
  return calm ? Math.min(duration, CALM_MS) : duration
}

// Which picture of a timelapse copy the video shows at `seconds`.
export function pictureAt(seconds, frameSeconds) {
  return Math.floor(seconds / frameSeconds + 1e-3)
}

export default class PanoramaVideo {
  constructor(canvas, video, { onError, onFrame, onTap, onHover } = {}) {
    this.canvas = canvas
    this.video = video
    this.onError = onError
    this.onFrame = onFrame
    this.onTap = onTap
    this.onHover = onHover
    this.yaw = 0
    this.pitch = 0
    this.fov = 75
    this.running = false
    this.drag = null
    this.gl = null
    this.frameSeconds = 0
    this.travel = [0, 0, -1]
    this.current = 0
    this.pictureIndex = null
    this.filled = false
    this.fade = null
    this.pending = null
    this.calm = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    this.levelFor = null
    this.levelNow = IDENTITY
    this.levelBefore = IDENTITY
    this.turnNow = 0
  }

  // Level and steady video (#6): `levelFor(seconds)` gives { matrix, turn }
  // for the picture at that moment - the turn that brings it back level and
  // takes out how far the camera had turned in the hand (lib/level.js
  // levelMatrix), and that turn in radians - or null. Steady, the view's yaw
  // (this.yaw) is counted from where the camera faced at the start; what's
  // drawn over the view (onFrame), lookAt and `looking` still speak of yaw
  // from the middle of the picture on screen.
  setLevel(levelFor) {
    this.levelFor = typeof levelFor === "function" ? levelFor : null
    this.levelNow = IDENTITY
    this.levelBefore = IDENTITY
    this.turnNow = 0
    this.pending = this.video.currentTime || 0 // redraw this moment with it
    this.pictureIndex = null
  }

  // A frame the track says nothing about keeps the last one's.
  _levelAt(seconds) {
    const at = this.levelFor && this.levelFor(seconds)
    if (!at) return this.levelFor ? { matrix: this.levelNow, turn: this.turnNow } : { matrix: IDENTITY, turn: 0 }
    return { matrix: at.matrix || IDENTITY, turn: Number.isFinite(at.turn) ? at.turn : 0, backward: at.backward }
  }

  _turnAt(seconds) {
    return this.levelFor ? this._levelAt(seconds).turn : 0
  }

  // A timelapse copy: each picture is held frameSeconds, and the video's
  // centre faced away from the way they walked when `backward` (a raw .insv,
  // see lib/walk_path.js). 0 for ordinary video: no glides.
  setPictures({ frameSeconds = 0, backward = false } = {}) {
    this.frameSeconds = Number(frameSeconds) > 0 ? Number(frameSeconds) : 0
    this.travel = [0, 0, backward ? 1 : -1]
    this.pictureIndex = null
    this.fade = null
  }

  // True when WebGL is available and the first frame could be uploaded.
  start() {
    if (!this.gl && !this._setup()) return false
    if (this.running) return true
    this.running = true
    this.pending = this.video.currentTime || 0
    this._bindPointer()
    this._watchFrames()
    this._loop()
    return true
  }

  stop() {
    this.running = false
    cancelAnimationFrame(this._frame)
    this._unbindPointer()
    this._unwatchFrames()
    this.fade = null
  }

  destroy() {
    this.stop()
    if (this.gl) {
      const ext = this.gl.getExtension("WEBGL_lose_context")
      ext?.loseContext()
    }
    this.gl = null
  }

  // yawDeg from the middle of the picture showing at the video's moment.
  lookAt(yawDeg, pitchDeg, fovDeg = null) {
    this.yaw = (yawDeg * Math.PI) / 180 + this._turnAt(this.video.currentTime || 0)
    this.pitch = Math.max(-85, Math.min(85, pitchDeg)) * (Math.PI / 180)
    if (Number.isFinite(fovDeg)) this.fov = Math.max(30, Math.min(110, fovDeg))
  }

  // Which way the view looks, in degrees (the yaw grows to the left).
  get looking() {
    return { yaw: ((this.yaw - this.turnNow) * 180) / Math.PI, pitch: (this.pitch * 180) / Math.PI, fov: this.fov }
  }

  // The view on screen as a JPEG data URL ("Save this view", Site Walks list
  // #8/#14): drawn once more, without a glide half done, and read straight
  // back before the browser clears the canvas. null where it can't be read.
  snapshot(quality = 0.9) {
    if (!this.gl) return null
    this.fade = null
    this._draw()
    try {
      return this.canvas.toDataURL("image/jpeg", quality)
    } catch {
      return null
    }
  }

  // ── WebGL ─────────────────────────────────────────────────────────────

  _setup() {
    const gl = this.canvas.getContext("webgl", { antialias: false, preserveDrawingBuffer: false }) ||
               this.canvas.getContext("experimental-webgl")
    if (!gl) return this._fail("WebGL is not available in this browser")

    const program = gl.createProgram()
    for (const [type, source] of [[gl.VERTEX_SHADER, VERTEX], [gl.FRAGMENT_SHADER, FRAGMENT]]) {
      const shader = gl.createShader(type)
      gl.shaderSource(shader, source)
      gl.compileShader(shader)
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return this._fail(gl.getShaderInfoLog(shader))
      gl.attachShader(program, shader)
    }
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return this._fail(gl.getProgramInfoLog(program))
    gl.useProgram(program)

    // Two triangles covering the whole canvas.
    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW)
    const position = gl.getAttribLocation(program, "position")
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)

    // Two textures: the picture on screen, and the one before it while a
    // timelapse glides from one to the next. Video frames are not
    // power-of-two sized: clamp + linear, no mipmaps.
    this.textures = [0, 1].map(() => {
      const texture = gl.createTexture()
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, 1, 1, 0, gl.RGB, gl.UNSIGNED_BYTE, new Uint8Array([20, 20, 20]))
      return texture
    })

    this.gl = gl
    this.uniforms = {
      yaw: gl.getUniformLocation(program, "yaw"),
      pitch: gl.getUniformLocation(program, "pitch"),
      tanHalfFov: gl.getUniformLocation(program, "tanHalfFov"),
      aspect: gl.getUniformLocation(program, "aspect"),
      blend: gl.getUniformLocation(program, "blend"),
      leanNow: gl.getUniformLocation(program, "leanNow"),
      leanBefore: gl.getUniformLocation(program, "leanBefore"),
      travel: gl.getUniformLocation(program, "travel"),
      levelNow: gl.getUniformLocation(program, "levelNow"),
      levelBefore: gl.getUniformLocation(program, "levelBefore")
    }
    gl.uniform1i(gl.getUniformLocation(program, "frame"), 0)
    gl.uniform1i(gl.getUniformLocation(program, "previous"), 1)
    return true
  }

  // ── New frames ────────────────────────────────────────────────────────
  // The browser says when the video shows a new frame (and when a seek or a
  // load lands); the next draw sends that one frame to the graphics card.
  // Where it can't say (older browsers), every draw of a playing video does.

  _watchFrames() {
    const video = this.video
    this._onLanded = () => { this.pending = video.currentTime || 0 }
    video.addEventListener("seeked", this._onLanded)
    video.addEventListener("loadeddata", this._onLanded)
    if (typeof video.requestVideoFrameCallback !== "function") return
    const presented = (_now, meta) => {
      if (!this.running) return
      this.pending = Number.isFinite(meta?.mediaTime) ? meta.mediaTime : video.currentTime || 0
      this._frameHandle = video.requestVideoFrameCallback(presented)
    }
    this._frameHandle = video.requestVideoFrameCallback(presented)
  }

  _unwatchFrames() {
    const video = this.video
    if (this._onLanded) {
      video.removeEventListener("seeked", this._onLanded)
      video.removeEventListener("loadeddata", this._onLanded)
      this._onLanded = null
    }
    if (this._frameHandle != null) video.cancelVideoFrameCallback?.(this._frameHandle)
    this._frameHandle = null
  }

  // Sends the video's frame to the graphics card - for a timelapse, only when
  // it's a new picture, which then glides in over the last one.
  _upload(mediaTime) {
    const { gl, video } = this
    if (this.frameSeconds > 0) {
      const index = pictureAt(mediaTime, this.frameSeconds)
      if (this.filled && index === this.pictureIndex) return true
      if (this.filled && this.pictureIndex != null) {
        const step = index - this.pictureIndex
        this.current = 1 - this.current // the picture on screen becomes the one before
        this.levelBefore = this.levelNow
        this._startGlide(Math.sign(step), Math.abs(step) > JUMP_PICTURES)
      }
      this.pictureIndex = index
    }
    const level = this._levelAt(mediaTime)
    this.levelNow = level.matrix
    this.turnNow = level.turn
    // Which way the camera was held at this picture (K10-4): the glide leans that way.
    if (typeof level.backward === "boolean") this.travel = [0, 0, level.backward ? 1 : -1]
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.textures[this.current])
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, video)
    } catch (error) {
      // A video loaded without CORS taints the texture: give up cleanly.
      this.stop()
      this._fail(error.message)
      return false
    }
    this.filled = true
    return true
  }

  _startGlide(direction, jump) {
    const { paused, playbackRate } = this.video
    const duration = glideDuration({ paused, frameSeconds: this.frameSeconds, playbackRate, calm: this.calm })
    this.fade = { start: performance.now(), duration, direction: direction || 1, jump: jump || this.calm }
  }

  // Where the glide is: how much of the new picture shows, and how far each leans.
  _glide() {
    const glide = glideAt(this.fade, performance.now())
    if (!glide) this.fade = null
    return glide || { blend: 1, leanNow: 0, leanBefore: 0 }
  }

  _fail(message) {
    this.onError?.(message)
    return false
  }

  _loop() {
    if (!this.running) return
    this._draw()
    this._frame = requestAnimationFrame(() => this._loop())
  }

  _draw() {
    const { gl, canvas, video } = this
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const width = Math.max(1, Math.round(canvas.clientWidth * dpr))
    const height = Math.max(1, Math.round(canvas.clientHeight * dpr))
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
    }
    gl.viewport(0, 0, width, height)

    if (video.readyState >= 2) {
      // No word from the browser about new frames: a playing video may have one.
      if (this._frameHandle == null && (!video.paused || !this.filled)) this.pending ??= video.currentTime || 0
      if (this.pending != null) {
        const at = this.pending
        this.pending = null
        if (!this._upload(at)) return
      }
    }

    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, this.textures[1 - this.current])
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.textures[this.current])
    const glide = this._glide()
    gl.uniform1f(this.uniforms.blend, glide.blend)
    gl.uniform1f(this.uniforms.leanNow, glide.leanNow)
    gl.uniform1f(this.uniforms.leanBefore, glide.leanBefore)
    gl.uniform3fv(this.uniforms.travel, this.travel)
    gl.uniformMatrix3fv(this.uniforms.levelNow, false, this.levelNow)
    gl.uniformMatrix3fv(this.uniforms.levelBefore, false, this.levelBefore)
    gl.uniform1f(this.uniforms.yaw, this.yaw)
    gl.uniform1f(this.uniforms.pitch, this.pitch)
    gl.uniform1f(this.uniforms.tanHalfFov, Math.tan((this.fov * Math.PI) / 360))
    gl.uniform1f(this.uniforms.aspect, width / height)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
    this.onFrame?.({ yaw: this.yaw - this.turnNow, pitch: this.pitch, fov: this.fov, width, height, dpr })
  }

  // ── Looking around ────────────────────────────────────────────────────

  _bindPointer() {
    const c = this.canvas
    const local = (event) => {
      const box = c.getBoundingClientRect()
      return [event.clientX - box.left, event.clientY - box.top]
    }
    this._onDown = (event) => {
      c.setPointerCapture?.(event.pointerId)
      this.drag = { x: event.clientX, y: event.clientY, yaw: this.yaw, pitch: this.pitch, at: Date.now() }
    }
    this._onMove = (event) => {
      if (!this.drag) {
        this.onHover?.(...local(event))
        return
      }
      // A full drag across the canvas turns you by the field of view. The
      // picture follows the hand both ways, like grabbing the scene (K6-4):
      // yaw grows to the left, so dragging right adds to it.
      const perPixel = (this.fov * Math.PI) / 180 / c.clientWidth
      this.yaw = this.drag.yaw + (event.clientX - this.drag.x) * perPixel
      const pitch = this.drag.pitch + (event.clientY - this.drag.y) * perPixel
      this.pitch = Math.max(-85, Math.min(85, (pitch * 180) / Math.PI)) * (Math.PI / 180)
    }
    this._onUp = (event) => {
      const drag = this.drag
      this.drag = null
      // Barely moved and quickly let go: a click, not a look around.
      if (drag && event?.type === "pointerup" && Date.now() - drag.at < 500 &&
          Math.hypot(event.clientX - drag.x, event.clientY - drag.y) < 6) {
        this.onTap?.(...local(event))
      }
    }
    this._onWheel = (event) => {
      event.preventDefault()
      this.fov = Math.max(30, Math.min(110, this.fov + event.deltaY * 0.05))
    }
    c.addEventListener("pointerdown", this._onDown)
    c.addEventListener("pointermove", this._onMove)
    c.addEventListener("pointerup", this._onUp)
    c.addEventListener("pointercancel", this._onUp)
    c.addEventListener("wheel", this._onWheel, { passive: false })
  }

  _unbindPointer() {
    const c = this.canvas
    if (!this._onDown) return
    c.removeEventListener("pointerdown", this._onDown)
    c.removeEventListener("pointermove", this._onMove)
    c.removeEventListener("pointerup", this._onUp)
    c.removeEventListener("pointercancel", this._onUp)
    c.removeEventListener("wheel", this._onWheel)
    this._onDown = null
  }
}
